'use client';

import { useEffect, useRef } from 'react';
import { useToast } from '@/components/ui/ToastNotifications';

// Simulated pipeline events that fire after mount to demonstrate live state transitions
const PIPELINE_EVENTS: Array<{
  delay: number;
  type: 'approved' | 'rendering' | 'published';
  title: string;
  message: string;
}> = [
  {
    delay: 3500,
    type: 'approved',
    title: 'LLM Hallucination Rate Drop',
    message: 'Script passed review · 147 words · ready for render queue',
  },
  {
    delay: 8000,
    type: 'rendering',
    title: 'Mistral 7B vs Llama 3.1',
    message: 'TTS narration started · estimated 2m 10s',
  },
  {
    delay: 14000,
    type: 'published',
    title: 'Claude 3.5 Sonnet vs GPT-4o',
    message: 'Posted to Instagram · auto-DM rules activated',
  },
  {
    delay: 22000,
    type: 'approved',
    title: 'Google Gemini 2.0 Flash Demo',
    message: 'Script passed review · 144 words · queued for render',
  },
  {
    delay: 30000,
    type: 'rendering',
    title: 'Fine-Tuning vs RAG',
    message: 'GPT Image generation started · stitching 9:16 frame',
  },
  {
    delay: 40000,
    type: 'published',
    title: 'OpenAI o3 Reasoning Model',
    message: 'Posted to Instagram · 312 views in first 5 min',
  },
];

// After initial burst, repeat a rolling cycle every ~45s
const ROLLING_EVENTS: typeof PIPELINE_EVENTS = [
  {
    delay: 0,
    type: 'approved',
    title: 'Cursor AI vs GitHub Copilot',
    message: 'Script passed review · 153 words · entering render queue',
  },
  {
    delay: 7000,
    type: 'rendering',
    title: 'Anthropic Constitutional AI',
    message: 'Render started · TTS + image pipeline active',
  },
  {
    delay: 16000,
    type: 'published',
    title: 'Perplexity AI Deep Research',
    message: 'Posted to Instagram · auto-DM rules live',
  },
];

export default function PipelineToastWatcher() {
  const { addToast } = useToast();
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    // Fire initial burst of events
    PIPELINE_EVENTS.forEach(({ delay, type, title, message }) => {
      const t = setTimeout(() => addToast(type, title, message), delay);
      timersRef.current.push(t);
    });

    // After initial burst, set up a rolling interval cycle
    const CYCLE_DURATION = 45000;
    let cycleStart = Date.now() + CYCLE_DURATION;

    function scheduleCycle() {
      const now = Date.now();
      const offset = cycleStart - now;
      ROLLING_EVENTS.forEach(({ delay, type, title, message }) => {
        const t = setTimeout(() => addToast(type, title, message), offset + delay);
        timersRef.current.push(t);
      });
      cycleStart += CYCLE_DURATION;
      const nextCycle = setTimeout(scheduleCycle, CYCLE_DURATION);
      timersRef.current.push(nextCycle);
    }

    const cycleTimer = setTimeout(scheduleCycle, CYCLE_DURATION);
    timersRef.current.push(cycleTimer);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [addToast]);

  return null;
}
